import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CloudPreviewDevSession,
  CloudPreviewPushEvent,
  DevSession,
  PreviewAddPhoneResult,
} from '@volley/crucible-ipc-types/ipc-channels';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { HARD_PREVIEW_PHONE_CAP } from '../workspace/preview-controller-policy';
import { useCrucibleClient } from '../transport/context';

/**
 * Pod-preview Slag Phase A (carve TDD §3.2): a ready session is
 * EITHER the local `DevSession` (display + controller iframes, ports)
 * OR the intentional cloud shape (`runtime: 'cloud'`, single
 * display-only `previewUrl`). Consumers narrow on
 * {@link isCloudPreviewSession} before touching `.urls`/`.ports`.
 */
export type AnyPreviewSession = DevSession | CloudPreviewDevSession;

/** True for the cloud-preview shape. The `runtime` discriminator is
 *  set ONLY by the main-side cloud branch — local payloads never
 *  carry it (the flag-OFF byte-identity invariant). */
export function isCloudPreviewSession(
  session: AnyPreviewSession,
): session is CloudPreviewDevSession {
  return 'runtime' in session && session.runtime === 'cloud';
}

/** The display URL to iframe, for either session shape (cloud →
 *  `previewUrl`; local → `urls.display`). */
export function previewDisplayUrl(session: AnyPreviewSession): string {
  return isCloudPreviewSession(session) ? session.previewUrl : session.urls.display;
}

/**
 * Pod-preview Slag Phase B (issue #2038) — derive the same-origin
 * controller URL for a cloud session from its display `previewUrl`.
 *
 * The Furnace pod serves the controller same-origin (live-proven on
 * `furnace-cole-0`/`dark-arena-80`, #1932 comment 4747172512): the
 * hello-weekend display Vite ships `base: /<gameId>/` and proxies
 * `/<gameId>/controller/` → the controller dev server (`127.0.0.1:5174`),
 * so `GET <origin>/<gameId>/controller/` returns the controller HTML
 * verbatim. This mirrors the LOCAL convention in
 * `crucible-voice/src/server/preview/build-urls.ts`
 * (`…/<gameId>/controller/?sessionId=<sid>`).
 *
 * The cloud `previewUrl` is the host-only external base plus a
 * pod-minted `?sessionId=<id>` query (TDD §3.2a(a):
 * `https://<furnace>.foundry-staging.weekend.com/?sessionId=<id>`). The
 * controller MUST boot under that SAME sessionId to join the same VGF
 * session, so the query is carried across verbatim — exactly as the
 * local display + controller URLs share a sessionId.
 *
 * Derivation is done with the URL constructor (never string-concat) so
 * base path + trailing-slash + query edge cases resolve correctly:
 *   - the path is absolute (`/<gameId>/controller/`), so it replaces
 *     the previewUrl's own path regardless of whether that was `/` or
 *     already carried a `/<gameId>/` base;
 *   - the gameId is percent-encoded (defensive — game ids are slugs,
 *     but a stray reserved char must not break the path);
 *   - the previewUrl's search (the `?sessionId=`) is copied onto the
 *     controller URL; an empty/absent query yields a bare
 *     `…/controller/` with no trailing `?`.
 *
 * Returns `null` when `previewUrl` is unparseable (a malformed pod
 * payload) — the caller then leaves `controllerUrls` empty and the
 * PHONE pane keeps its idle placeholder rather than iframing a broken
 * src.
 */
export function cloudControllerUrl(session: CloudPreviewDevSession): string | null {
  try {
    const display = new URL(session.previewUrl);
    const controller = new URL(
      `/${encodeURIComponent(session.gameId)}/controller/`,
      display.origin,
    );
    // Preserve the pod-minted sessionId (and any other query the pod
    // attached) so the controller joins the SAME VGF session as the
    // display. `display.search` is '' when there's no query → no
    // dangling '?'.
    controller.search = display.search;
    return controller.toString();
  } catch {
    return null;
  }
}

/**
 * Pod-preview multi-controller (cloud) — derive one same-origin controller
 * URL per stable pane id. Each is the base
 * `/<gameId>/controller/?sessionId=<sid>` (see {@link cloudControllerUrl})
 * with a distinct, STABLE identity per pane.
 *
 * Identity — not position — is the source of truth, and we set ALL THREE
 * identity params (`volley_account`, `userId`, `testUserId`) to the same
 * per-pane value, because the param a controller honours differs by template
 * AND by template VERSION:
 *   - the current spine `resolveControllerUserId` reads
 *     `userId ?? testUserId ?? volley_account ?? deviceId ?? fallbackId`;
 *   - a game scaffolded BEFORE that resolver landed (hw #1529/#1530) reads
 *     only `volley_account`;
 *   - some templates (e.g. guess-the-item) read `userId`; older ones read
 *     `testUserId`.
 * Setting one param only would seat distinct VGF members on some templates
 * but silently collapse all N iframes onto a shared `deviceId` (ONE player)
 * on others — the shadow-player bug. Setting all three seats a distinct VGF
 * member DETERMINISTICALLY on every controller version, with no auth
 * side-effect: `volley_account` is a tracking-only id and controller
 * account-auth has not landed (hello-weekend PlayingController). This mirrors
 * the LOCAL path (`dev-preview-urls.ts`), which emits multiple identity params
 * for the same "contract differs by template" reason. (Credit: chloevolley,
 * #2266 review / #2180 de-risk.)
 *
 * The ids are stable across remove: a pane keeps its exact id (and therefore
 * its exact URL / iframe `src` / VGF member) when a sibling is removed, so
 * the kept panes never churn identity. The pod's display Vite proxies
 * `/<gameId>/controller/` to the controller dev server same-origin, so NO
 * extra Slag routing or main-side IPC is needed — these URLs are purely
 * client-derived.
 *
 * Returns `[]` when `testUserIds` is empty or the base URL is unparseable.
 */
export function deriveCloudControllerUrls(
  session: CloudPreviewDevSession,
  testUserIds: readonly string[],
): string[] {
  const base = cloudControllerUrl(session);
  if (base === null) return [];
  const out: string[] = [];
  for (const id of testUserIds) {
    const u = new URL(base);
    // All three identity params → the SAME value. The param a controller
    // honours differs by template + version (see the docblock): the new spine
    // resolver reads userId/testUserId first, but older games read only
    // volley_account. Belt-and-suspenders seats a distinct VGF member on all.
    u.searchParams.set('volley_account', id);
    u.searchParams.set('userId', id);
    u.searchParams.set('testUserId', id);
    out.push(u.toString());
  }
  return out;
}

/**
 * Lifecycle wrapper around the `dev:start` / `dev:stop` / `preview:restart`
 * IPCs for the Preview overlay (#199 / `docs/_archive/plan-preview-overlay.md`,
 * extended for restart/reload in #643).
 *
 * State machine:
 *   - `idle` — never started, or `dev:stop` has resolved. Call
 *     `start()` to launch.
 *   - `busy` — `dev:start`, `preview:restart`, or `dev:stop` in flight.
 *     The `operation` field is the operational truth; product copy and CTA
 *     priority live in `derivePreviewDevLoopState`.
 *   - `ready` — IPC resolved with a `DevSession`. URLs available to
 *     mount iframes against. The `reloadKey` counter rides alongside —
 *     consumers should fold it into iframe `key` props so a `reload()`
 *     call remounts the iframes against the same URLs (issue #643's
 *     "lighter-weight Reload preview" affordance for stale WebView
 *     state).
 *   - `error` — IPC rejected. Holds the structured `SerialisedError`, the
 *     operation that failed, and the previous ready session when there was
 *     one. Call `start()` again to retry.
 *
 * Concurrent-call guard: if a second lifecycle call fires while we're
 * already `busy`, the second call is a no-op — the in-flight IPC's resolution
 * is what matters. This avoids duplicate `dev:start` / `preview:restart` /
 * `dev:stop` round-trips if the user mashes controls.
 *
 * Renderer-side state only — no persistence. The orchestrator's
 * authoritative state lives at `.foundry/dev-status.json` (read by
 * `/diagnose`); this hook is just our caller-side mirror.
 */
export type PreviewSessionState =
  | { status: 'idle' }
  | {
      status: 'busy';
      gameId: string;
      operation: PreviewSessionOperation;
      previousSession?: AnyPreviewSession;
    }
  | { status: 'ready'; session: AnyPreviewSession }
  | {
      status: 'error';
      gameId: string;
      operation: PreviewSessionOperation;
      error: SerialisedError;
      previousSession?: AnyPreviewSession;
    };

function stateOwnsControllerSession(
  state: PreviewSessionState,
  gameId: string,
  sessionId: string,
): boolean {
  const session =
    state.status === 'ready'
      ? state.session
      : state.status === 'busy' || (state.status === 'error' && state.operation === 'starting')
        ? state.previousSession
        : undefined;
  return session?.gameId === gameId && session.sessionId === sessionId;
}

export type PreviewSessionOperation = 'starting' | 'restarting' | 'stopping';

/**
 * True when the ready session is a PAIRED preview (Weekend-app pairing /
 * `paired-server-only`) — LOCAL or CLOUD. A cloud paired preview
 * (crucible#pairing) carries `previewMode: 'paired-server-only'` +
 * `pairing`, so it's recognised exactly as a local paired session: the
 * paired surface (PairingPanel), NOT the multi-controller affordances.
 * A classic cloud session carries neither, so it stays multi-controller.
 */
export function isPairedPreviewActive(state: PreviewSessionState): boolean {
  if (state.status !== 'ready') return false;
  return state.session.previewMode === 'paired-server-only' || state.session.pairing !== undefined;
}

/**
 * Gate for the +Add / −Remove / per-pane-✕ phone affordances. Enabled for a
 * ready session that is NOT a paired preview — covering BOTH the local
 * multi-phone path AND the cloud multi-controller path (each cloud pane gets
 * its own same-origin `testUserId`). The Workspace passes a defined
 * `onAddPhone`/`onRemovePhone`/`onRemovePhoneAt` exactly when this is true,
 * so the buttons render; the hook then grows/shrinks the controller array.
 *
 * Regression guard (multi-controller blocker M2): the first cut additionally
 * excluded cloud sessions here, making the feature unreachable — the cloud
 * `+` button never rendered. Cloud is intentionally INCLUDED now.
 */
export function previewPhoneControlsEnabled(state: PreviewSessionState): boolean {
  return state.status === 'ready' && !isPairedPreviewActive(state);
}

export interface PreviewStartOptions {
  phoneCount?: number;
  maxControllerSlots?: number;
  pairing?: boolean;
}

export interface PreviewAddPhoneOptions {
  maxControllerSlots?: number;
}

/**
 * Issue #643 — result shape returned by `restart()`. The state
 * branches already carry success / error info, but consumers that
 * want to surface a toast (the Workspace's `previewToast`) need an
 * explicit signal that can't be confused with the always-resolves
 * Promise contract. Discriminated by `ok` so the call site stays
 * type-safe — Codex review #1 BLOCK fix.
 */
export type RestartResult =
  | { ok: true }
  | { ok: false; error: SerialisedError }
  | { ok: false; reason: 'busy' };

export interface PreviewRestartOptions {
  maxControllerSlots?: number;
}

/**
 * Track B (multi-phone preview) — result shape returned by
 * `addPhone()`. Mirrors the IPC layer's
 * {@link PreviewAddPhoneResult} so the caller can disable the
 * "+ Add Phone" button on cap_reached, surface a "session ended"
 * toast on no_session, or push the new URL into its array on ok.
 */
export type AddPhoneResult = PreviewAddPhoneResult;

export interface PreviewSession {
  state: PreviewSessionState;
  /**
   * Track B — the current set of controller URLs to mount. Always at
   * least one entry once `state.status === 'ready'` (the initial
   * `dev:start` mints `foundry-preview-1`). `addPhone()` appends; the
   * cap is MAX_PREVIEW_PHONES (6). `removePhone()` drops the last.
   *
   * Empty when the session isn't ready (idle / starting / error) so
   * consumers can render placeholder UI without a "is the array
   * populated yet?" check.
   */
  controllerUrls: string[];
  /**
   * Monotonic counter incremented by `reload()`. Consumers (e.g.
   * `<PreviewOverlay>` iframe panes) include this in the `key` prop
   * so React remounts the iframes against the same URLs — the
   * cheapest equivalent of a `Cmd-Shift-R` hard-refresh given the
   * panes are plain `<iframe>` elements, not Electron `<webview>`
   * tags (issue #643 — the lighter-weight "Reload preview" branch).
   */
  reloadKey: number;
  /**
   * Fire `dev:start` for the given gameId. No-op when already
   * `busy`. Re-fires from `idle` / `ready` / `error` so the
   * caller can retry after a failure.
   */
  start: (gameId: string, options?: PreviewStartOptions) => Promise<void>;
  /**
   * Fire `dev:stop` for the current session. No-op when no session
   * is running. Drops state back to `idle` on success. On IPC
   * rejection main keeps the active session and paired tunnel, so
   * the hook restores `ready` so the UI stays aligned and stop can
   * be retried.
   */
  stop: () => Promise<void>;
  /**
   * Issue #643 — fire `preview:restart` for the given gameId.
   * SIGTERMs the three child processes, relaunches via the same
   * orchestrator path `dev:start` uses, and returns a fresh
   * `DevSession` (new sessionId, possibly new ports). Behaves like
   * `start()` when no session is currently running. Concurrent-call
   * guarded by an immediate `inFlightRef` (sync write before setState
   * — protects against two same-tick calls observing the stale
   * snapshot of `state.status`).
   *
   * Returns a discriminated {@link RestartResult} so callers can
   * surface a success / failure toast without wrapping the call in
   * try/catch — the previous `Promise<void>` shape silently
   * swallowed errors via the state's `'error'` branch and any
   * `await restart(); toast('ok')` chain misreported failures as
   * successes (Codex review #1 BLOCK).
   */
  restart: (gameId: string, options?: PreviewRestartOptions) => Promise<RestartResult>;
  /**
   * Issue #643 — bump the `reloadKey` so iframe consumers remount.
   * No IPC traffic; entirely renderer-side. Best for the common
   * "the dev server is fine but the iframe's cached state is stale"
   * case (hot-reload didn't take, controller stuck on a dead VGF
   * session, etc.). Returns synchronously.
   */
  reload: () => void;
  /**
   * Hard-reset state to `idle` without firing any IPC. Used by the
   * step-6 "Dismiss error" path so the overlay can drop the error
   * banner without nuking a still-running orchestrator state.
   */
  reset: () => void;
  /**
   * Track B — fire `preview:add-phone` and, on success, append the
   * returned URL to {@link controllerUrls}. Returns the IPC result
   * so callers can disable the "+ Add Phone" button on cap_reached
   * (rather than the renderer optimistically appending and then
   * waiting for the next render to learn the cap was hit).
   *
   * No-ops + returns `{ ok: false, reason: 'no_session' }` when no
   * session is ready (defensive against a stale-click race during a
   * restart window).
   */
  addPhone: (options?: PreviewAddPhoneOptions) => Promise<AddPhoneResult>;
  /**
   * Track B — fire `preview:remove-phone` and drop the LAST entry
   * from {@link controllerUrls}. Returns `false` (no-op) when the
   * array is already at the minimum (length 1) — the overlay's
   * "− Remove Phone" button is disabled in that state.
   *
   * The corresponding iframe unmounts as a React effect; the
   * iframe's VGF socket disconnects on its own. The IPC round-trip
   * is for symmetry with `addPhone()` (and future telemetry).
   */
  removePhone: () => Promise<boolean>;
  /**
   * Track B — remove a SPECIFIC phone by index (the per-screen ✕ on
   * the phone strip). `preview:remove-phone` is index-agnostic (it only
   * decrements the session's slot count for cap bookkeeping), so this
   * splices `controllerUrls` at `index`; the unmounted iframe drops its
   * own VGF socket. Returns `false` (no-op) at the minimum (length 1) or
   * for an out-of-range index. Remaining phones keep their baked-in
   * testUserIds (so position labels may renumber while sockets persist).
   */
  removePhoneAt: (index: number) => Promise<boolean>;
}

export function usePreviewSession(): PreviewSession {
  const client = useCrucibleClient();
  const [state, setState] = useState<PreviewSessionState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * Track B — the array of controller URLs driving the multi-phone
   * surface. Seeded from `dev:start`'s reply (always at least one
   * entry, `foundry-preview-1`), then mutated by `addPhone()` /
   * `removePhone()`.
   */
  const [controllerUrls, setControllerUrls] = useState<string[]>([]);
  /**
   * Track B — monotonic counter so removed phones never have their
   * testUserId reused. Starts at 1 (matching `foundry-preview-1` on
   * the initial start), increments on every successful `addPhone()`.
   * Never decreases on `removePhone()` — even after removing every
   * extra phone, the next add gets a fresh id. Keeps VGF members
   * unambiguous through churn.
   */
  const nextTestUserIdSeqRef = useRef(1);
  /**
   * Pod-preview multi-controller (cloud) — the source of truth for which
   * cloud controller panes exist AND their STABLE testUserIds (e.g.
   * `cloud-phone-1`, `cloud-phone-2`, …), in pane order. The cloud branch
   * derives `controllerUrls` from these ids via
   * {@link deriveCloudControllerUrls}, so identity (not positional count)
   * drives the iframe `src`. Each id is minted from the monotonic
   * `nextTestUserIdSeqRef` and never reused, so a removed pane's id can't
   * collide with a future add, and the KEPT panes keep their exact
   * id/URL/VGF-member across a sibling removal. Empty for a local
   * (non-cloud) or not-yet-started session.
   */
  const cloudPhoneIdsRef = useRef<string[]>([]);
  // Snapshot of the most recent state passed via the closure so the
  // concurrent-call guard reads the latest value, not the stale
  // closure-captured one. (React's useState exposes the setter's
  // updater form for this; we use a ref because the guard fires
  // BEFORE we'd otherwise mutate state.)
  const stateRef = useRef(state);
  stateRef.current = state;

  // Codex review #1 HIGH: `state.status === 'busy'` lags by a
  // render cycle, so two same-tick calls (mash the button hard, or
  // restart firing during a `start()` in flight) can both pass the
  // state-based guard. An immediate ref written BEFORE setState
  // closes the gap — atomic in the JS single-thread sense.
  const inFlightRef = useRef(false);
  // Controller add/remove IPC is not covered by the launch lock. Advance this
  // synchronously whenever lifecycle ownership changes so a late controller
  // response cannot mutate the next session before React commits its state.
  // A lifecycle attempt alone does not change ownership: main may reject it
  // and retain the same live session. Advance only when a session is actually
  // adopted, dropped, or explicitly reset.
  const lifecycleEpochRef = useRef(0);

  // Pod-preview Slag Phase A — buffer for `preview:cloud-update` pushes
  // that land in the narrow window between the `dev:start` bridge
  // resolving the cloud session (its FIRST ready `preview-url` chunk)
  // and THIS hook committing `status: 'ready'`. The subscription below
  // only applies an update when `status === 'ready'`; a `url-swap` or a
  // terminal `closed` fanned in that gap was previously DROPPED — so the
  // session kept its first URL with no later correction, or stayed
  // "ready" pointing at a pod whose dev server had already died. (Codex
  // residual on #1948 — the loss was at this HOOK boundary, identical on
  // the desktop `cloud-preview-launch` path and the browser SDK bridge,
  // since both resolve-on-first-ready into this same hook.)
  //
  // Keyed by sessionId; latest-wins per session (a later `url-swap`
  // supersedes an earlier one; a terminal `closed` supersedes a pending
  // `url-swap`). Replayed on adoption (`start()` / `restart()`), then
  // pruned. Cleared at the head of each launch so a superseded session's
  // buffered events can't leak into the next adoption.
  const pendingCloudUpdatesRef = useRef<Map<string, CloudPreviewPushEvent>>(new Map());

  // Pod-preview multi-controller — seed `cloudPhoneIdsRef` to a single
  // `cloud-phone-1` on a FRESH cloud adoption (the ref is empty after
  // stop()/initial mount), or PRESERVE the existing ids on a re-adoption
  // (start re-fired, restart, or url-swap) so added phones survive. Returns
  // the current ids array. Stable — refs only.
  const seedOrPreserveCloudPhoneIds = useCallback((): string[] => {
    if (cloudPhoneIdsRef.current.length === 0) {
      cloudPhoneIdsRef.current = ['cloud-phone-1'];
      // The seq tracks the NEXT id to mint, so a fresh seed of
      // `cloud-phone-1` leaves the next addPhone at `cloud-phone-2`.
      nextTestUserIdSeqRef.current = 2;
    }
    return cloudPhoneIdsRef.current;
  }, []);

  // Apply a cloud-update to a KNOWN-adopted cloud session. Shared by the
  // live subscription path (session read from `stateRef`) and the
  // adoption replay path (session passed explicitly, because `stateRef`
  // still lags the just-issued `setState('ready')`). The setters are
  // referentially stable, so this is too.
  const applyCloudUpdate = useCallback(
    (event: CloudPreviewPushEvent, session: CloudPreviewDevSession) => {
      if (event.kind === 'url-swap') {
        const swapped: CloudPreviewDevSession = { ...session, previewUrl: event.previewUrl };
        setState({ status: 'ready', session: swapped });
        // Pod-preview multi-controller — re-derive the controller URLs off
        // the swapped display `previewUrl` (the swap may carry a fresh
        // `?sessionId=`), PRESERVING the existing pane identities so the
        // user's added phones keep their exact testUserIds (and VGF
        // members) across the swap. A null base / empty ids → empty array
        // (idle placeholder) rather than a stale src.
        setControllerUrls(deriveCloudControllerUrls(swapped, seedOrPreserveCloudPhoneIds()));
        // Remount the iframe against the new URL — same mechanism as
        // restart's reloadKey bump.
        setReloadKey((k) => k + 1);
      } else {
        // `closed` — the pod dev-server stream ended. Drop to idle so the
        // overlay shows the start affordance rather than a dead iframe.
        // Clear the cloud pane identities + seq so the NEXT cloud adoption
        // is a fresh single `cloud-phone-1`, not a re-seed of stale ids.
        setState({ status: 'idle' });
        setControllerUrls([]);
        cloudPhoneIdsRef.current = [];
        nextTestUserIdSeqRef.current = 1;
      }
    },
    [seedOrPreserveCloudPhoneIds],
  );

  // On adoption, replay the latest buffered update (if any) for the
  // session we just adopted, then drop it from the buffer.
  const flushPendingCloudUpdate = useCallback(
    (session: CloudPreviewDevSession) => {
      const pending = pendingCloudUpdatesRef.current.get(session.sessionId);
      if (pending === undefined) return;
      pendingCloudUpdatesRef.current.delete(session.sessionId);
      applyCloudUpdate(pending, session);
    },
    [applyCloudUpdate],
  );

  // Pod-preview Slag Phase A (carve TDD D9) — subscribe to the
  // session-scoped `preview:cloud-update` channel. The main-side
  // orchestrator pushes `url-swap` when the pod dev server restarts
  // onto a new external URL, and `closed` when the stream ends. Both
  // are filtered by sessionId: a push whose sessionId differs from
  // the currently-adopted ready session is DROPPED, so a superseded
  // consumer (from a prior restart) can never retarget the live
  // iframe. Subscribe once; the callback reads `stateRef.current` for
  // the live session rather than closing over a stale snapshot.
  useEffect(() => {
    const unsubscribe = client.subscribe('preview:cloud-update', (event) => {
      const current = stateRef.current;
      if (current.status === 'ready' && isCloudPreviewSession(current.session)) {
        if (event.sessionId !== current.session.sessionId) {
          // Stale / cross-session push against the LIVE session — drop
          // (TDD D9). A superseded consumer can never retarget the iframe.
          return;
        }
        applyCloudUpdate(event, current.session);
        return;
      }
      // Not yet adopted (busy / idle / error, or a non-cloud ready
      // session): BUFFER keyed by sessionId so adoption can replay it.
      // Without this, a push fanned between the bridge's first-ready
      // resolve and this hook's `status: 'ready'` commit is lost (Codex
      // residual on #1948). Adoption only replays the entry whose
      // sessionId it adopts, so a genuinely stale buffered session sits
      // inert until the next launch clears the buffer.
      pendingCloudUpdatesRef.current.set(event.sessionId, event);
    });
    return unsubscribe;
  }, [applyCloudUpdate, client]);

  // Commit-time replay (Codex P2 on #1951). The imperative flush in
  // `start()` / `restart()` fires at the `setState('ready')` CALL, but
  // `stateRef` only catches up on the React COMMIT — so a push landing in
  // that post-flush/pre-commit sliver would take the non-ready
  // (buffering) path with no later replay. This effect runs ON the ready
  // commit (when `stateRef` is current and the live subscription path is
  // active) and drains any buffered update for the adopted session,
  // fully closing the window. Idempotent with the imperative flush:
  // whichever runs first deletes the buffer entry, so it applies exactly
  // once. A `url-swap` reply re-renders and re-runs this effect, but the
  // entry is already gone → no-op, no loop.
  useEffect(() => {
    if (state.status !== 'ready' || !isCloudPreviewSession(state.session)) return;
    flushPendingCloudUpdate(state.session);
  }, [state, flushPendingCloudUpdate]);

  useEffect(() => {
    const unsubscribe = client.subscribe('preview:pairing-lost', ({ gameId }) => {
      setState((current) => {
        if (current.status !== 'ready' || current.session.gameId !== gameId) {
          return current;
        }
        // Both LOCAL and CLOUD paired sessions carry previewMode/pairing
        // (crucible#pairing / Bugbot #2918): a tunnel death strips BOTH so
        // the PairingPanel stops showing a dead tunnel URL — cloud sessions
        // are no longer excluded. A session carrying neither is already
        // non-paired, so there's nothing to strip.
        if (current.session.pairing === undefined && current.session.previewMode === undefined) {
          return current;
        }
        const {
          pairing: _pairing,
          previewMode: _previewMode,
          ...sessionWithoutPairing
        } = current.session;
        return {
          status: 'ready',
          session: sessionWithoutPairing,
        };
      });
    });
    return () => {
      unsubscribe();
    };
  }, [client]);

  const start = useCallback(
    async (gameId: string, options: PreviewStartOptions = {}) => {
      if (inFlightRef.current || stateRef.current.status === 'busy') {
        // In-flight call already running. Don't double-fire.
        return;
      }
      inFlightRef.current = true;
      // Discard any cloud-updates buffered against a superseded session;
      // the event(s) for the session we're about to adopt arrive AFTER the
      // `dev:start` invoke below begins streaming.
      pendingCloudUpdatesRef.current.clear();
      const previousSession =
        stateRef.current.status === 'ready' && stateRef.current.session.gameId === gameId
          ? stateRef.current.session
          : undefined;
      setState({
        status: 'busy',
        gameId,
        operation: 'starting',
        ...(previousSession ? { previousSession } : {}),
      });
      try {
        const session = await client.invoke('dev:start', {
          gameId,
          ...(options.phoneCount !== undefined ? { phoneCount: options.phoneCount } : {}),
          ...(options.maxControllerSlots !== undefined
            ? { maxControllerSlots: options.maxControllerSlots }
            : {}),
          ...(options.pairing !== undefined ? { pairing: options.pairing } : {}),
        });
        lifecycleEpochRef.current += 1;
        setState({ status: 'ready', session });
        if (isCloudPreviewSession(session)) {
          // Pod-preview multi-controller — the Furnace pod serves every
          // controller same-origin, so derive each pane's URL from the
          // display `previewUrl` keyed by a STABLE testUserId. A fresh start
          // (ids empty after stop()) seeds ONE `cloud-phone-1`; a re-adopt
          // PRESERVES the existing ids so added phones (and their VGF
          // members) survive. addPhone()/removePhone() grow/shrink the id
          // array client-side. A null derivation (unparseable previewUrl)
          // leaves the array empty → idle PHONE placeholder, no broken src.
          setControllerUrls(deriveCloudControllerUrls(session, seedOrPreserveCloudPhoneIds()));
          // Replay any cloud-update fanned before we committed `ready`.
          flushPendingCloudUpdate(session);
        } else {
          // Track B — seed the multi-phone array from the initial reply.
          // `controllers` is always length >= 1 (main mints
          // foundry-preview-1 for the single-phone start), and we reset
          // the seq to one-past-the-initial-count so the NEXT addPhone
          // mints foundry-preview-${len+1}.
          setControllerUrls(session.urls.controllers);
          nextTestUserIdSeqRef.current = session.urls.controllers.length + 1;
          // Adopting a LOCAL session must also drop any CLOUD pane ids — the
          // two refs move together (cf. stop()). Otherwise stale cloudPhoneIds
          // survive while the seq is reset DOWN to the local count, and a later
          // cloud re-adopt would preserve those ids yet let addPhone mint an
          // already-used cloud-phone-N → duplicate VGF identity across panes.
          cloudPhoneIdsRef.current = [];
        }
      } catch (err: unknown) {
        setState({
          status: 'error',
          gameId,
          operation: 'starting',
          error: unflattenIpcError(err),
          ...(previousSession ? { previousSession } : {}),
        });
      } finally {
        inFlightRef.current = false;
      }
    },
    [client, flushPendingCloudUpdate, seedOrPreserveCloudPhoneIds],
  );

  const stop = useCallback(async () => {
    const current = stateRef.current;
    if (inFlightRef.current || current.status === 'busy') {
      return;
    }
    const previousSession =
      current.status === 'ready'
        ? current.session
        : current.status === 'error' && current.operation === 'starting'
          ? current.previousSession
          : undefined;
    if (!previousSession) {
      // Nothing running. Idempotent no-op.
      setState({ status: 'idle' });
      return;
    }
    const sessionId = previousSession.sessionId;
    const gameId = previousSession.gameId;
    inFlightRef.current = true;
    setState({
      status: 'busy',
      gameId,
      operation: 'stopping',
      previousSession,
    });
    try {
      // Codex review #4 round-6 MEDIUM — pass gameId so main can
      // acquire the per-game launch lock BEFORE doing the
      // sessionId lookup, closing the map-empty race window with
      // an in-flight preview:restart.
      await client.invoke('dev:stop', { sessionId, gameId });
      lifecycleEpochRef.current += 1;
      setState({ status: 'idle' });
      // Track B — drop the multi-phone state when the session ends.
      // Next start() will reseed from the fresh reply (local) or a fresh
      // single `cloud-phone-1` (cloud).
      setControllerUrls([]);
      nextTestUserIdSeqRef.current = 1;
      cloudPhoneIdsRef.current = [];
    } catch {
      // Main keeps activeDevSessions and any paired tunnel when
      // stopDevSession fails (dev-stop.ts catch branch). Mirror
      // that here so a later stop() can retry against the same sessionId.
      // Preserve an error that retained the session instead of promoting it
      // back to ready; the failed launch is still the renderer truth.
      setState(
        current.status === 'error' ? current : { status: 'ready', session: previousSession },
      );
    } finally {
      inFlightRef.current = false;
    }
  }, [client]);

  const restart = useCallback(
    async (gameId: string, options?: PreviewRestartOptions): Promise<RestartResult> => {
      if (inFlightRef.current || stateRef.current.status === 'busy') {
        // In-flight `start()` or `restart()` already running. Don't
        // double-fire — the main-side handler is idempotent in shape
        // (it stops then starts) but a second concurrent call would
        // race the SIGTERM and could leave the second one observing
        // mid-relaunch state. Keeping the renderer-side guard pins
        // the contract that exactly one launch promise is ever
        // in-flight.
        //
        // Returns `{ ok: false, reason: 'busy' }` (NOT throwing) so
        // the Workspace's restart handler can distinguish "Foundry
        // ate this click because another launch was already happening"
        // from "the IPC actually failed". The button mash case
        // doesn't deserve an error toast.
        return { ok: false, reason: 'busy' };
      }
      inFlightRef.current = true;
      // Discard cloud-updates buffered against the session being torn
      // down; the new session's events arrive after the relaunch below.
      pendingCloudUpdatesRef.current.clear();
      // Codex review #4 round-4 HIGH + round-5 MEDIUM — snapshot the
      // previous `ready` session BEFORE flipping to `starting` so we
      // can restore it on the ONE specific failure where main keeps
      // `activeDevSessions` intact: CRUCIBLE-FOUNDRY-PREVIEW-RESTART-
      // STRAGGLER. That's the SIGKILL-survivor branch in the main
      // handler — the old processes are still alive, the old session
      // entry is still the authoritative state, and the user is told
      // to "click Restart preview again". Every OTHER restart failure
      // mode (port-conflict relaunch, pnpm install crash, workspace-
      // build error) tears down the old session before throwing, so
      // restoring `previousReady` would leave the UI pointing at dead
      // URLs.
      //
      // Codex round-5 MEDIUM also flagged the missing gameId guard:
      // a route/caller race where the user switched games mid-restart
      // could resolve THIS gameId's failure but restore a session
      // belonging to a DIFFERENT (now-stale) game. Pin the snapshot
      // to the gameId actually being restarted.
      const previousReady =
        stateRef.current.status === 'ready' && stateRef.current.session.gameId === gameId
          ? stateRef.current.session
          : null;
      const operation: PreviewSessionOperation = previousReady === null ? 'starting' : 'restarting';
      setState({
        status: 'busy',
        gameId,
        operation,
        ...(previousReady ? { previousSession: previousReady } : {}),
      });
      try {
        const session = await client.invoke('preview:restart', {
          gameId,
          ...(options?.maxControllerSlots !== undefined
            ? { maxControllerSlots: options.maxControllerSlots }
            : {}),
        });
        lifecycleEpochRef.current += 1;
        setState({ status: 'ready', session });
        if (isCloudPreviewSession(session)) {
          // Pod-preview multi-controller — re-derive the controller URLs
          // off the relaunched display `previewUrl` (the restart may carry
          // a fresh sessionId in the query), PRESERVING the existing pane
          // identities across the restart so added phones keep their exact
          // testUserIds (and VGF members). Null base / empty ids → empty
          // (idle placeholder).
          setControllerUrls(deriveCloudControllerUrls(session, seedOrPreserveCloudPhoneIds()));
          // Replay any cloud-update fanned before we committed `ready`.
          flushPendingCloudUpdate(session);
        } else {
          // Track B — restart is a full session cycle. Drop the
          // previous multi-phone array and reseed from the new reply.
          // Any extra phones the user had added are deliberately
          // dropped (the orchestrator's processes were SIGTERMed, so
          // the iframes would be staring at dead sockets anyway).
          setControllerUrls(session.urls.controllers);
          nextTestUserIdSeqRef.current = session.urls.controllers.length + 1;
          // Same desync guard as start(): a local-session restart must also
          // clear cloud pane ids so a later cloud re-adopt can't preserve
          // stale ids while addPhone re-mints a colliding cloud-phone-N.
          cloudPhoneIdsRef.current = [];
        }
        // Bump reloadKey on a successful restart so any iframes that
        // happened to already be mounted (race: overlay open while
        // restart races to completion) drop their cached frame and
        // remount against the new sessionId-bearing URLs.
        setReloadKey((k) => k + 1);
        return { ok: true };
      } catch (err: unknown) {
        const serialised = unflattenIpcError(err);
        // Round-5 MEDIUM — narrow restoration to ONLY the straggler
        // code. Other retryable codes (e.g. transient network) tear
        // down the old session in main before throwing, so the old
        // URLs are dead — surfacing the error card is the right call.
        const isStragglerFailure = serialised.code === 'CRUCIBLE-FOUNDRY-PREVIEW-RESTART-STRAGGLER';
        if (isStragglerFailure && previousReady !== null) {
          setState({ status: 'ready', session: previousReady });
        } else {
          // Every non-straggler restart failure tears down the previous
          // session in main. Invalidate controller responses that still
          // carry that now-dead ownership generation.
          lifecycleEpochRef.current += 1;
          setState({
            status: 'error',
            gameId,
            operation,
            error: serialised,
            ...(previousReady ? { previousSession: previousReady } : {}),
          });
        }
        return { ok: false, error: serialised };
      } finally {
        inFlightRef.current = false;
      }
    },
    [client, flushPendingCloudUpdate, seedOrPreserveCloudPhoneIds],
  );

  const reload = useCallback(() => {
    // Pure renderer-side affordance. Even when no session is ready
    // we still bump the counter — it's a cheap monotonic counter,
    // and the iframe panes only paint when URLs are present, so a
    // bump-with-no-URLs is a harmless no-op that we don't need to
    // guard against.
    setReloadKey((k) => k + 1);
  }, []);

  const reset = useCallback(() => {
    inFlightRef.current = false;
    lifecycleEpochRef.current += 1;
    setState({ status: 'idle' });
    // Mirror stop(): clear controller state so a stale count/identity
    // can't leak into the next retry. With cloud identity preservation,
    // leaving `controllerUrls` / `cloudPhoneIdsRef` populated would make
    // the next cloud start re-seed N stale panes instead of a fresh one.
    setControllerUrls([]);
    cloudPhoneIdsRef.current = [];
    nextTestUserIdSeqRef.current = 1;
  }, []);

  /**
   * Track B — fire `preview:add-phone` and append the new URL to
   * `controllerUrls` on success. Reads `controllerUrls` and the
   * monotonic seq counter via refs so the IPC call carries the
   * current snapshot, not a stale closure capture.
   */
  const controllerUrlsRef = useRef(controllerUrls);
  controllerUrlsRef.current = controllerUrls;

  const addPhone = useCallback(
    async (options?: PreviewAddPhoneOptions): Promise<AddPhoneResult> => {
      const current = stateRef.current;
      if (current.status !== 'ready') {
        // No session — render-side guard. The "+ Add Phone" button is
        // hidden until status flips to 'ready', but a stale click during
        // a restart window could land here.
        return { ok: false, reason: 'no_session' };
      }
      if (isCloudPreviewSession(current.session)) {
        // Pod-preview multi-controller: the pod serves every controller
        // same-origin (`/<gameId>/controller/`), so additional panes are
        // derived CLIENT-SIDE (a distinct, STABLE testUserId per pane →
        // distinct iframe + distinct VGF client) with no main-side IPC.
        // Mint a fresh id from the monotonic seq (never reused, so a
        // removed pane's id can't collide with a future add), append it to
        // the identity ref, re-derive, and return the new pane's URL + id.
        // The `+` button is already gated on the policy `maxControllerSlots`;
        // this cap is the backstop.
        const cloudSession = current.session;
        const cap = Math.max(
          1,
          Math.min(HARD_PREVIEW_PHONE_CAP, options?.maxControllerSlots ?? HARD_PREVIEW_PHONE_CAP),
        );
        if (cloudPhoneIdsRef.current.length + 1 > cap) {
          return {
            ok: false,
            reason: 'cap_reached',
            currentPhoneCount: cloudPhoneIdsRef.current.length,
            maxControllerSlots: cap,
            hardCap: HARD_PREVIEW_PHONE_CAP,
          };
        }
        const id = `cloud-phone-${nextTestUserIdSeqRef.current}`;
        const nextIds = [...cloudPhoneIdsRef.current, id];
        const urls = deriveCloudControllerUrls(cloudSession, nextIds);
        if (urls.length < nextIds.length) {
          // Unparseable previewUrl — can't derive a new controller src.
          // Leave the identity ref untouched so we don't strand an id.
          return { ok: false, reason: 'no_session' };
        }
        cloudPhoneIdsRef.current = nextIds;
        nextTestUserIdSeqRef.current += 1;
        setControllerUrls(urls);
        return { ok: true, url: urls[urls.length - 1]!, testUserId: id };
      }
      if (current.session.previewMode === 'paired-server-only' || current.session.pairing) {
        return { ok: false, reason: 'paired_session' };
      }
      const gameId = current.session.gameId;
      const sessionId = current.session.sessionId;
      const lifecycleEpoch = lifecycleEpochRef.current;
      const currentPhoneCount = controllerUrlsRef.current.length;
      const nextTestUserIdSeq = nextTestUserIdSeqRef.current;
      let result: AddPhoneResult;
      try {
        result = await client.invoke('preview:add-phone', {
          gameId,
          currentPhoneCount,
          nextTestUserIdSeq,
          ...(options?.maxControllerSlots !== undefined
            ? { maxControllerSlots: options.maxControllerSlots }
            : current.session.maxControllerSlots !== undefined
              ? { maxControllerSlots: current.session.maxControllerSlots }
              : {}),
        });
      } catch {
        // The IPC channel itself failed (renderer-main bridge down,
        // serialisation crash). Surface as 'no_session' — same UX
        // outcome from the caller's perspective: don't append, don't
        // surface a generic toast.
        return { ok: false, reason: 'no_session' };
      }
      const latest = stateRef.current;
      if (
        lifecycleEpochRef.current !== lifecycleEpoch ||
        !stateOwnsControllerSession(latest, gameId, sessionId)
      ) {
        // The IPC belonged to a session that was stopped/replaced while the
        // request was in flight. Never append its controller to the new
        // game's shared renderer state.
        return { ok: false, reason: 'no_session' };
      }
      if (result.ok) {
        setControllerUrls((prev) => [...prev, result.url]);
        nextTestUserIdSeqRef.current += 1;
      }
      return result;
    },
    [client],
  );

  /**
   * Track B — fire `preview:remove-phone` and drop the LAST entry
   * from `controllerUrls` only after main acknowledges the same
   * current count. The corresponding iframe unmounts as a React
   * effect; the iframe's VGF socket disconnects on its own.
   *
   * No-ops when the array is already at the minimum (length 1) —
   * the overlay's "− Remove Phone" button is disabled in that state,
   * but this is belt-and-braces.
   */
  const removePhoneAt = useCallback(
    async (index: number): Promise<boolean> => {
      const current = stateRef.current;
      if (current.status !== 'ready') return false;
      if (isCloudPreviewSession(current.session)) {
        // Pod-preview multi-controller: drop the client-derived controller
        // pane AT `index` (no main-side IPC — cloud controllers aren't
        // main-managed). Splice that pane's STABLE id out of the identity
        // ref and re-derive; the KEPT panes retain their exact ids/URLs/VGF
        // members (only the removed one's iframe unmounts and drops its
        // socket). Identity, not position, is preserved.
        const cloudSession = current.session;
        const count = cloudPhoneIdsRef.current.length;
        if (count <= 1) return false;
        if (!Number.isInteger(index) || index < 0 || index >= count) return false;
        cloudPhoneIdsRef.current = cloudPhoneIdsRef.current.filter((_, i) => i !== index);
        setControllerUrls(deriveCloudControllerUrls(cloudSession, cloudPhoneIdsRef.current));
        return true;
      }
      if (current.session.previewMode === 'paired-server-only' || current.session.pairing) {
        return false;
      }
      const currentPhoneCount = controllerUrlsRef.current.length;
      if (currentPhoneCount <= 1) return false;
      if (!Number.isInteger(index) || index < 0 || index >= currentPhoneCount) return false;
      const gameId = current.session.gameId;
      const sessionId = current.session.sessionId;
      const lifecycleEpoch = lifecycleEpochRef.current;
      try {
        const result = await client.invoke('preview:remove-phone', {
          gameId,
          currentPhoneCount,
        });
        if (!result.ok) return false;
      } catch {
        // Keep renderer and main in sync. If main did not accept the
        // removal, do not prune the local URL or the session slot counts
        // can drift apart and mis-apply controller caps.
        return false;
      }
      const latest = stateRef.current;
      if (
        lifecycleEpochRef.current !== lifecycleEpoch ||
        !stateOwnsControllerSession(latest, gameId, sessionId)
      ) {
        // A newer session now owns controllerUrls; the old removal response
        // must not prune one of that session's panes.
        return false;
      }
      setControllerUrls((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
      return true;
    },
    [client],
  );

  // `removePhone()` is the index-agnostic shim — drop the LAST entry.
  const removePhone = useCallback(
    (): Promise<boolean> => removePhoneAt(controllerUrlsRef.current.length - 1),
    [removePhoneAt],
  );

  return {
    state,
    controllerUrls,
    reloadKey,
    start,
    stop,
    restart,
    reload,
    reset,
    addPhone,
    removePhone,
    removePhoneAt,
  };
}
